export type AdminToolCategory = 'Payouts' | 'Briefs' | 'Super Socials' | 'Creators' | 'STATS' | 'Shopify' | 'General';

export type AdminToolId =
  | 'payout-retry-fix'
  | 'brand-vouchers'
  | 'creator-payment-details-fix'
  | 'brief-application-cleanup'
  | 'brief-tracker'
  | 'super-socials'
  | 'creator-manual-registration'
  | 'creator-stats'
  | 'business-stats'
  | 'reports'
  | 'sandbox-user-config';

export interface AdminToolContextRequirements {
  briefId: boolean;
  creatorId: boolean;
}

export interface AdminToolDef {
  id: AdminToolId;
  label: string;
  category: AdminToolCategory;
  description: string;
  context: AdminToolContextRequirements;
}

export const ADMIN_TOOL_REGISTRY: readonly AdminToolDef[] = [
  {
    id: 'payout-retry-fix',
    label: 'Payout trace & retry',
    category: 'Payouts',
    description: 'Trace creator/brief → payout chain, then retry by payout ID.',
    context: { briefId: true, creatorId: true },
  },
  {
    id: 'brand-vouchers',
    label: 'Brand vouchers',
    category: 'Payouts',
    description: 'List every brand voucher and disable active ones so they expire immediately.',
    context: { briefId: false, creatorId: false },
  },
  {
    id: 'creator-payment-details-fix',
    label: 'Creator payment details',
    category: 'Payouts',
    description: 'Inspect creator_payment_detail, validate and create Airwallex beneficiaries (GodMode).',
    context: { briefId: false, creatorId: true },
  },
  {
    id: 'brief-tracker',
    label: 'Brief tracker',
    category: 'Briefs',
    description:
      'Inspect brief, application status history, shout brief config, and submitted posts by creator and brief ID.',
    context: { briefId: true, creatorId: true },
  },
  {
    id: 'brief-application-cleanup',
    label: 'Duplicate applications',
    category: 'Briefs',
    description:
      'Find duplicate brief applications for a creator, remove stale rows and release wallet reservations.',
    context: { briefId: true, creatorId: true },
  },
  {
    id: 'super-socials',
    label: 'Super Socials',
    category: 'Super Socials',
    description:
      'Look up a creator in SuperSocial by Shout UID, view connected accounts, and refresh orchestrator data.',
    context: { briefId: false, creatorId: true },
  },
  {
    id: 'creator-manual-registration',
    label: 'Register creator',
    category: 'Creators',
    description:
      'Create or repair a Shout creator record for an existing Firebase user who failed app signup.',
    context: { briefId: false, creatorId: false },
  },
  {
    id: 'creator-stats',
    label: 'Creator stats',
    category: 'STATS',
    description:
      'Paginated creator engagement counters: search appearances, profile visits, invites, and hires.',
    context: { briefId: false, creatorId: false },
  },
  {
    id: 'business-stats',
    label: 'Business stats',
    category: 'STATS',
    description:
      'Daily per-business activity: search sessions, profile visits, invites sent, and hires (UTC).',
    context: { briefId: false, creatorId: false },
  },
  {
    id: 'reports',
    label: 'Reports',
    category: 'STATS',
    description:
      'Generate and save period reports: creators joined, briefs posted, creators hired, brands joined.',
    context: { briefId: false, creatorId: false },
  },
  {
    id: 'sandbox-user-config',
    label: 'Sandbox user',
    category: 'General',
    description:
      'Enable sandbox review mode for a brand user and configure per-feature permissions and creator allowlist.',
    context: { briefId: false, creatorId: false },
  },
] as const;

export const DEFAULT_ADMIN_TOOL_ID: AdminToolId = 'payout-retry-fix';

/** URL segment under `/admin-options/settings/` for each tool. */
export const ADMIN_TOOL_ROUTE_SEGMENTS: Record<AdminToolId, string> = {
  'payout-retry-fix': 'payout-retry-fix',
  'brand-vouchers': 'brand-vouchers',
  'creator-payment-details-fix': 'creator-payment-details',
  'brief-application-cleanup': 'brief-application-cleanup',
  'brief-tracker': 'brief-tracker',
  'super-socials': 'super-socials',
  'creator-manual-registration': 'register-creator',
  'creator-stats': 'creator-stats',
  'business-stats': 'business-stats',
  reports: 'reports',
  'sandbox-user-config': 'sandbox-user',
};

export const ADMIN_STATS_TOOL_IDS = [
  'creator-stats',
  'business-stats',
  'reports',
] as const satisfies readonly AdminToolId[];

export type AdminStatsToolId = (typeof ADMIN_STATS_TOOL_IDS)[number];

export function getAdminSettingsToolPath(toolId: AdminToolId): string {
  return `/admin-options/settings/${ADMIN_TOOL_ROUTE_SEGMENTS[toolId]}`;
}

export function getAdminSettingsToolLink(toolId: AdminToolId): string {
  const base = getAdminSettingsToolPath(toolId);
  if (toolId === 'creator-stats' || toolId === 'business-stats' || toolId === 'reports') {
    return `${base}/dashboard`;
  }
  if (toolId === 'brand-vouchers') {
    return `${base}/new`;
  }
  return base;
}

export function isAdminToolRouteActive(toolId: AdminToolId, url: string): boolean {
  const path = getAdminSettingsToolPath(toolId);
  return url === path || url.startsWith(`${path}/`);
}

export const ADMIN_TOOL_CATEGORY_ORDER: readonly AdminToolCategory[] = [
  'Briefs',
  'Payouts',
  'Super Socials',
  'Creators',
  'STATS',
  'Shopify',
  'General',
] as const;

export interface AdminToolCategoryGroup {
  category: AdminToolCategory;
  tools: AdminToolDef[];
}

export function getAdminToolsGroupedByCategory(): AdminToolCategoryGroup[] {
  return ADMIN_TOOL_CATEGORY_ORDER.map((category) => ({
    category,
    tools: ADMIN_TOOL_REGISTRY.filter((tool) => tool.category === category),
  })).filter((group) => group.tools.length > 0);
}

export function getAdminToolById(id: AdminToolId): AdminToolDef | undefined {
  return ADMIN_TOOL_REGISTRY.find((tool) => tool.id === id);
}

export function isAdminToolId(value: string): value is AdminToolId {
  return ADMIN_TOOL_REGISTRY.some((tool) => tool.id === value);
}

export function getActiveAdminToolId(url: string): AdminToolId | null {
  for (const tool of ADMIN_TOOL_REGISTRY) {
    if (isAdminToolRouteActive(tool.id, url)) {
      return tool.id;
    }
  }
  return null;
}

export function getActiveAdminTool(url: string): AdminToolDef | undefined {
  const id = getActiveAdminToolId(url);
  return id ? getAdminToolById(id) : undefined;
}
